import { Room, Opening, point, zones } from './model'
import { isCustomLayout, customAccessWarnings, customGeometry } from './custom-layout'
import { bedLocalToRoom } from './bunk-ladder-plan'
import { hasAssignedStairs } from './bunk-access'
export type Bed = {
  id: string
  name: string
  basicFinish?: 'matte_white' | 'gloss_white' | 'custom_color'
  basicColorCode?: string
  kind?: 'stairs' | 'bridge' | 'custom'
  customStyle?: 'rosemary' | 'divider' | 'inlet' | 'watercolor' | 'scenic'
  mattressConfig?: 'twin_full' | 'twinxl_queen'
  /** Divider-layout lower perpendicular bed width. */
  lowerBedSize?: 'twin' | 'full'
  upperBedSize?: 'twin' | 'twinxl'
  ladderWidth?: number
  dividerWidth?: number
  lowerLeftOffset?: number
  lowerRightOffset?: number
  /** Shallow, three-bay cabinet between the floating-cabin lower beds. */
  lowerShiplap?: boolean
  builtInBookshelf?: boolean
  bookshelfStyle?: 'standard' | 'recessed_arch'
  x: number
  y: number
  /** Designer-set assembly dimensions; preserve on reopening and room edits. */
  designerDimensions?: boolean
  /** Per-design compact stair dimensions, in inches; omitted values retain legacy geometry. */
  stairLandingDepth?: number
  stairTreadRun?: number
  /** Stair-only experiments; upper-bunk elevations remain independent. */
  stairRise?: number
  stairRiserCount?: number
  /** Upper frame depth; legacy plans retain their 12-inch frame. */
  upperFrameDepth?: number
  /** Independent surround and mattress-support offsets above the deck. */
  upperFrameRise?: number
  upperMattressLift?: number
  windowGuardSolid?: boolean
  width: number
  depth: number
  height: number
  rotation: 0 | 90 | 180 | 270
  /** Older plans include the ladder unless explicitly removed. */
  ladder?: boolean
  /** Staircase assignment controls access choices, not verified landing geometry. */
  servesBedIds?: string[]
  /** Optional appearance; legacy stairs retain their enclosure. */
  stairStyle?: 'enclosed' | 'open' | 'stringer'
}
export const BED_OPTIONS = [
  { name: 'Twin over Twin', width: 44, depth: 82 },
  { name: 'Twin XL over Twin XL', width: 44, depth: 87 },
  { name: 'Twin over Full', width: 60, depth: 82 },
  { name: 'Twin XL over Queen', width: 66, depth: 87 },
  { name: 'Queen over Queen', width: 66, depth: 87 },
  { name: 'Full over Full', width: 60, depth: 82 },
]
export function bounds(b: Bed) {
  return {
    x: b.x,
    y: b.y,
    w: b.rotation % 180 === 90 ? b.depth : b.width,
    h: b.rotation % 180 === 90 ? b.width : b.depth,
  }
}
export function positionBed(r: Room, b: Bed, x: number, y: number) {
  const q = bounds(b)
  const snap = (n: number, max: number) =>
    Math.max(
      0,
      Math.min(
        Math.max(0, max),
        Math.abs(n) < 2 ? 0 : Math.abs(n - max) < 2 ? max : Math.round(n * 2) / 2
      )
    )
  return { ...b, x: snap(x, r.width - q.w), y: snap(y, r.depth - q.h) }
}
// Test actual occupied parts, not the empty aisle inside a grouped layout.
function occupiedRects(b: Bed) {
  if (!isCustomLayout(b)) return [bounds(b)]
  const g = customGeometry(b),
    rects = [
      ...g.uppers,
      ...g.lowers,
      ...g.ladders,
      ...(g.divider ? [g.divider] : []),
      ...(g.landing ? [g.landing] : []),
      ...(g.stairs ? [g.stairs] : []),
    ]
  return rects.map(r => {
    const a = bedLocalToRoom(b, r.x, r.y),
      z = bedLocalToRoom(b, r.x + r.w, r.y + r.h)
    return {
      x: Math.min(a[0], z[0]),
      y: Math.min(a[1], z[1]),
      w: Math.abs(a[0] - z[0]),
      h: Math.abs(a[1] - z[1]),
    }
  })
}
function overlap(
  a: { x: number; y: number; w: number; h: number },
  b: { x: number; y: number; w: number; h: number }
) {
  return (
    a.x < b.x + b.w - 0.01 &&
    a.x + a.w > b.x + 0.01 &&
    a.y < b.y + b.h - 0.01 &&
    a.y + a.h > b.y + 0.01
  )
}
// Stair sides may sit against bunks; their approach ends still need clearance.
function stairAccessGap(a: Bed, b: Bed, beds: Bed[], axis: 'x' | 'y') {
  const stairs = [a, b].filter(piece => piece.kind === 'stairs')
  if (stairs.length)
    return stairs.length === 1 && (stairs[0].rotation % 180 === 0) === (axis === 'x')
  const first = bounds(a),
    second = bounds(b)
  const start =
    axis === 'x'
      ? Math.min(first.x + first.w, second.x + second.w)
      : Math.min(first.y + first.h, second.y + second.h)
  const end = axis === 'x' ? Math.max(first.x, second.x) : Math.max(first.y, second.y)
  const crossStart = axis === 'x' ? Math.max(first.y, second.y) : Math.max(first.x, second.x)
  const crossEnd =
    axis === 'x'
      ? Math.min(first.y + first.h, second.y + second.h)
      : Math.min(first.x + first.w, second.x + second.w)
  return beds.some(stair => {
    if (stair.kind !== 'stairs' || (stair.rotation % 180 === 0) !== (axis === 'x')) return false
    const q = bounds(stair)
    const low = axis === 'x' ? q.x : q.y,
      high = low + (axis === 'x' ? q.w : q.h),
      cross = axis === 'x' ? q.y : q.x,
      crossSize = axis === 'x' ? q.h : q.w
    return (
      low >= start - 0.01 &&
      high <= end + 0.01 &&
      cross < crossEnd &&
      cross + crossSize > crossStart
    )
  })
}
export function bedWarnings(r: Room, beds: Bed[], openings: Opening[]) {
  const results: { bedId: string; message: string }[] = []
  for (const bed of beds) {
    const b = bounds(bed),
      occupied = occupiedRects(bed),
      add = (message: string) => results.push({ bedId: bed.id, message: `${bed.name}: ${message}` })
    // Footprints do not describe guardrail openings or landing connections. Never
    // infer usable upper-bunk access from adjacency alone, even between two bunks.
    if (isCustomLayout(bed)) {
      for (const issue of customAccessWarnings(r, bed)) add(issue)
      if (r.studs !== 'wood')
        add('supporting wall construction must be confirmed for wall-stud anchorage.')
    }
    if (!bed.kind && bed.ladder === false && !hasAssignedStairs(beds, bed.id))
      add('no ladder or staircase is assigned. Add a ladder or choose this bunk under a staircase’s access settings.')
    if (bed.kind === 'stairs')
      add(
        'upper-bunk access needs review. The landing must align with an open entry into each bunk it serves, with no guardrail or panel blocking the way.'
      )
    if (b.x < 0 || b.y < 0 || b.x + b.w > r.width || b.y + b.h > r.depth)
      add('this footprint extends outside the room. Reduce its dimensions or rotate it.')
    if (bed.height >= r.height) add('the overall height reaches or exceeds the ceiling.')
    for (const o of openings) {
      const z = zones(r, o),
        p = point(r, o.wall, z.start, 0),
        q = point(r, o.wall, z.start + z.width, z.depth)
      const rect = {
        x: Math.min(p[0], q[0]),
        y: Math.min(p[1], q[1]),
        w: Math.abs(p[0] - q[0]),
        h: Math.abs(p[1] - q[1]),
      }
      if (occupied.some(piece => overlap(piece, rect)))
        add(
          o.kind === 'window'
            ? bed.height > o.sill
              ? 'overlaps the window access area and its height range. Review window operation and guardrails.'
              : 'occupies floor space below the window. Check access to its handle.'
            : `blocks ${o.purpose.toLowerCase()} access on Wall ${o.wall}. Keep the marked approach clear.`
        )
      if (o.kind === 'door' && o.operation === 'hinged' && o.swing === 'in') {
        const blocked = occupied.some(b => {
          const corners = [
            [b.x, b.y],
            [b.x + b.w, b.y],
            [b.x, b.y + b.h],
            [b.x + b.w, b.y + b.h],
          ].map(([x, y]) => {
            const u =
                o.wall === 'A'
                  ? x
                  : o.wall === 'B'
                    ? y
                    : o.wall === 'C'
                      ? r.width - x
                      : r.depth - y,
              v =
                o.wall === 'A' ? y : o.wall === 'B' ? r.width - x : o.wall === 'C' ? r.depth - y : x
            return [
              (u - o.offset - (o.hinge === 'right' ? o.width : 0)) * (o.hinge === 'right' ? -1 : 1),
              v,
            ]
          })
          const minU = Math.min(...corners.map(p => p[0])),
            maxU = Math.max(...corners.map(p => p[0])),
            minV = Math.min(...corners.map(p => p[1])),
            maxV = Math.max(...corners.map(p => p[1]))
          return maxU > 0 && maxV > 0 && Math.hypot(Math.max(0, minU), Math.max(0, minV)) < o.width
        })
        if (blocked)
          add(`intersects the inward door swing on Wall ${o.wall}. Move it so the door can open.`)
      }
    }
    for (const other of beds.filter(o => o.id !== bed.id)) {
      const c = bounds(other)
      if (occupied.some(a => occupiedRects(other).some(b => overlap(a, b))))
        add(`overlaps ${other.name}. Move the footprints apart.`)
      else {
        const gapX = Math.max(c.x - b.x - b.w, b.x - c.x - c.w),
          gapY = Math.max(c.y - b.y - b.h, b.y - c.y - c.h)
        if (
          (gapX > 0 &&
            gapX < 36 &&
            b.y < c.y + c.h &&
            b.y + b.h > c.y &&
            !stairAccessGap(bed, other, beds, 'x')) ||
          (gapY > 0 &&
            gapY < 36 &&
            b.x < c.x + c.w &&
            b.x + b.w > c.x &&
            !stairAccessGap(bed, other, beds, 'y'))
        )
          add(
            `has less than 36 inches between it and ${other.name}. If this is a walkway, widen it.`
          )
      }
    }
  }
  return results
}






